import { db, storage } from '../firebase';
import { collection, getDocs, getDoc, doc, addDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Song } from '../types';

export const getSongs = async (): Promise<Song[]> => {
  const songsCollection = collection(db, 'songs');
  const songsSnapshot = await getDocs(songsCollection);
  return songsSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt?.toDate()
  } as Song));
};

export const getSongById = async (id: string): Promise<Song | null> => {
  const songDoc = doc(db, 'songs', id);
  const songSnapshot = await getDoc(songDoc);
  
  if (!songSnapshot.exists()) return null;
  
  return {
    id: songSnapshot.id,
    ...songSnapshot.data(),
    createdAt: songSnapshot.data().createdAt?.toDate()
  } as Song;
};

export const uploadSong = async (
  file: File, 
  metadata: Omit<Song, 'id' | 'storageUrl' | 'downloadUrl' | 'createdAt'>
): Promise<Song> => {
  try {
    console.log('Starting song upload process');
    
    // Create a unique path for the song
    const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const storageRef = ref(storage, `songs/${fileName}`);
    console.log('Storage reference created:', storageRef.fullPath);
    
    // Upload file to Firebase Storage
    console.log('Uploading file to Firebase Storage...');
    const uploadResult = await uploadBytes(storageRef, file);
    console.log('Upload complete:', uploadResult);
    
    // Get the download URL
    console.log('Getting download URL...');
    const downloadUrl = await getDownloadURL(storageRef);
    console.log('Download URL obtained:', downloadUrl);
    
    // Create record in Firestore
    console.log('Creating Firestore record...');
    const newSong = {
      ...metadata,
      storageUrl: storageRef.fullPath,
      downloadUrl,
      createdAt: new Date()
    };
    
    const docRef = await addDoc(collection(db, 'songs'), newSong);
    console.log('Firestore record created with ID:', docRef.id);
    
    return {
      id: docRef.id,
      ...newSong
    } as Song;
  } catch (error) {
    console.error('Error in uploadSong:', error);
    // Re-throw the error with additional context
    throw new Error(`Failed to upload song: ${error instanceof Error ? error.message : String(error)}`);
  }
};

export const updateSong = async (id: string, updates: Partial<Song>): Promise<void> => {
  const songRef = doc(db, 'songs', id);
  await updateDoc(songRef, {
    ...updates,
    updatedAt: new Date()
  });
};

export const deleteSong = async (id: string): Promise<void> => {
  // Get the song to find the storage path
  const song = await getSongById(id);
  if (!song) return;
  
  // Delete from storage if it exists
  if (song.storageUrl) {
    const storageRef = ref(storage, song.storageUrl);
    await deleteObject(storageRef);
  }
  
  // Delete from Firestore
  await deleteDoc(doc(db, 'songs', id));
}; 